import React from 'react';
import FeedbackStarIcon from '../../../icons/FeedbackStartIcon';
import type { components } from '../../../../types/learnerApi';

type FeedbackItemType = components['schemas']['FeedbackItemType'];

export interface LikertAnswer {
  rating: number;
}

export interface LikertScaleViewProps {
  item: {
    id: string;
    type: FeedbackItemType;
    required?: boolean;
    config: { question?: string };
  };
  mode: 'learner' | 'preview';
  value?: LikertAnswer;
  onChange?: (value: LikertAnswer) => void;
  disabled?: boolean;
}

function scaleMax(type: FeedbackItemType): number {
  if (type === 'likert_3_scale') return 3;
  if (type === 'likert_10_scale') return 10;
  return 5;
}

const LikertScaleView: React.FC<LikertScaleViewProps> = ({
  item,
  mode,
  value,
  onChange,
  disabled,
}) => {
  const max = scaleMax(item.type);
  const selected = value?.rating ?? 0;

  const select = (rating: number) => {
    if (disabled || mode === 'preview') return;
    onChange?.({ rating });
  };

  return (
    <div className="space-y-3">
      {/* Question text is rendered by the parent FeedbackPage in learner
          mode. In preview mode the parent doesn't add chrome, so render
          it here so the admin can see the prompt. */}
      {mode === 'preview' && (
        <p className="font-medium text-text-primary">
          {item.config.question}
          {item.required && <span className="text-error-500 ml-1">*</span>}
        </p>
      )}
      <div
        className={`flex items-center justify-center ${
          max > 5 ? 'gap-1.5' : 'gap-2 sm:gap-3'
        } flex-wrap`}
        role="radiogroup"
        aria-label="Rating"
      >
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => {
          const filled = selected >= n;
          const starSize = max > 5 ? 'w-8 h-8 sm:w-9 sm:h-9' : 'w-10 h-10 sm:w-12 sm:h-12';
          return (
            <button
              key={n}
              type="button"
              onClick={() => select(n)}
              disabled={disabled}
              role="radio"
              aria-checked={selected === n}
              aria-label={`${n} of ${max}`}
              className={`shrink-0 rounded-full transition-[transform,color] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 ${
                filled ? 'text-warning-500' : 'text-text-tertiary/50'
              } ${
                disabled
                  ? 'cursor-not-allowed opacity-70'
                  : 'cursor-pointer hover:scale-105 active:scale-95'
              }`}
            >
              <FeedbackStarIcon className={starSize} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LikertScaleView;
